/**
 * APIクライアント（fetchラッパー）
 */
export const API_BASE = (import.meta as { env?: { VITE_API_ORIGIN?: string } }).env?.VITE_API_ORIGIN
  ?? '/api/v1';

export type ApiSuccess<T> = {
  success: true;
  data: T;
  meta: { request_id: string; timestamp: string };
};

export type ApiError = {
  success: false;
  error: { code: string; message: string; details?: unknown[] } & Record<string, unknown>;
  meta: { request_id: string; timestamp: string };
};

export class ApiClientError extends Error {
  // v0.5① 日報機能: 提出エラーサマリ (§14-1 #3) が個別項目へのアンカー付きリストを作るために
  // error.details ([{field, code, message}]) を必要とするため保持する (既存呼び出し元は
  // 3引数のまま呼べる後方互換な追加、既存の catch (e.message/e.code) は影響を受けない)。
  constructor(public status: number, public code: string, message: string, public details?: unknown[]) {
    super(message);
  }
}

/** ApiClientError.details ([{field, code, message}]) から特定フィールドのエラーメッセージを取り出す。
 *  v0.5 マスタ開始日指定機能: トースト表示だけでは「どの項目のエラーか」が伝わらないため、
 *  日付フィールド等にフィールド単位のフィードバックを添えるために使う。 */
export const getApiFieldError = (e: unknown, field: string): string | undefined => {
  if (!(e instanceof ApiClientError) || !Array.isArray(e.details)) return undefined;
  const hit = e.details.find((d): d is { field: string; message: string } =>
    typeof d === 'object' && d !== null && 'field' in d && (d as { field: unknown }).field === field);
  return hit?.message;
};

/** v0.3.x (AIトークン利用上限管理): UsageMeter.checkQuota() の violation を渡すレスポンス契約
 *  (apps/api/src/lib/errors.ts throwQuotaExceeded と対応)。 */
export type QuotaExceededInfo = {
  scope: string;
  scopeLabel: string;
  limitUsd: number;
  spentUsd: number;
  resetAt: string;
};

/** 429 + AI_QUOTA_EXCEEDED 時にポップアップを開くためのフック。registerAuthClearedHandler と
 *  同じ関数注入方式 (循環依存回避)。main.tsx から quotaModal ストアの open を注入する。 */
let onQuotaExceeded: ((info: QuotaExceededInfo) => void) | null = null;
export const registerQuotaExceededHandler = (fn: (info: QuotaExceededInfo) => void) => { onQuotaExceeded = fn; };

const getToken = (): string | null => localStorage.getItem('access_token');

export const setTokens = (access: string, refresh: string) => {
  localStorage.setItem('access_token', access);
  localStorage.setItem('refresh_token', refresh);
};

export const clearTokens = () => {
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
};

/** 401 時にトークン＋ユーザ状態を一括クリアするためのフック。
 *  main.tsx から Zustand ストアの setUser を注入する。
 *  ここで直接 import すると循環依存になるため関数注入方式にしている。 */
let onAuthCleared: (() => void) | null = null;
export const registerAuthClearedHandler = (fn: () => void) => { onAuthCleared = fn; };

/** apiFetch は Content-Type を強制 application/json にするため multipart (FormData) アップロードには
 *  使えず、呼び出し側が生 fetch() を書かざるを得ない (ImportsPage / ManualEntryPage の CSV upload 等)。
 *  そのケースでも 401 時のトークン＋ユーザ状態クリアだけは統一する共通ヘルパー (CLAUDE.md §8-4 対応)。 */
export const handleUnauthorized = (status: number) => {
  if (status !== 401) return;
  clearTokens();
  onAuthCleared?.();
};

export const apiFetch = async <T>(
  path: string,
  init: RequestInit = {},
): Promise<T> => {
  const token = getToken();
  const headers = new Headers(init.headers ?? {});
  headers.set('Content-Type', 'application/json');
  if (token) headers.set('Authorization', `Bearer ${token}`);

  const url = path.startsWith('http') ? path : `${API_BASE}${path}`;
  const res = await fetch(url, { ...init, headers });
  // 204 No Content はボディが空のため res.json() が例外を投げ、成功しているのに
  // catch側で「失敗しました」表示になる (DELETE系19エンドポイントに影響、v0.4追加要件⑩の
  // 実機検証で発見)。ステータスのみで判定しボディの解析をスキップする。
  if (res.status === 204) return undefined as T;
  const body = (await res.json()) as ApiSuccess<T> | ApiError;

  if (!res.ok || !body.success) {
    const err = (body as ApiError).error;
    if (res.status === 401) {
      clearTokens();
      // Zustand の user 状態もクリアし、ProtectedLayout に /login へ誘導させる
      onAuthCleared?.();
    }
    if (res.status === 429 && err?.code === 'AI_QUOTA_EXCEEDED') {
      onQuotaExceeded?.({
        scope: String(err.scope ?? ''),
        scopeLabel: String(err.scope_label ?? err.message ?? '利用上限'),
        limitUsd: Number(err.limit_usd ?? 0),
        spentUsd: Number(err.spent_usd ?? 0),
        resetAt: String(err.reset_at ?? ''),
      });
    }
    throw new ApiClientError(res.status, err?.code ?? 'UNKNOWN', err?.message ?? '不明なエラー', err?.details);
  }
  return (body as ApiSuccess<T>).data;
};
